import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Package } from 'lucide-react'

import type { WorkflowSearchTracker } from '@/lib/order-workflow-contract'
import type { KiotVietCodeName, Product } from '@/lib/api/product-api'
import { useDebounced } from '@/hooks/use-debounced'
import { useInfiniteProducts, useProduct } from '@/hooks/use-product-query'
import { useKiotVietCodeNames } from '@/hooks/use-kiotviet-code-names'
import { kiotVietLinkedLabel } from '@/lib/kiotviet-link'
import { Combobox } from '@/components/ui/combobox'
import { formatQty } from '@/lib/format'
import { getProductImageURL } from '@/lib/product-image'
import { m } from '@/lib/paraglide/messages'

/** Delay keystroke → API fire so typing "combo" doesn't hit /products 5 times. */
const SEARCH_DEBOUNCE_MS = 300
/** Cỡ trang mỗi lần cuộn (rule resource-select input: không nạp per_page lớn). */
const PAGE_SIZE = 30
const NO_PRODUCTS: Product[] = []
/** Số mã tối đa mỗi lần tra tên KiotViet (BE /kiotviet-products/names ≤ 200). */
const KIOTVIET_NAMES_BATCH = 200

interface Props {
    workflow?: WorkflowSearchTracker
    /**
     * Id của control thật, để `<label htmlFor>` trỏ đúng vào trigger. Bộ lọc
     * danh sách bắt buộc có nhãn hiện (R7) nên cần forward xuống Combobox;
     * các form khác không truyền thì vẫn như cũ.
     */
    id?: string
    value: string
    /** Emits the selected product's id. The full `Product` object is passed as
     *  a second arg so callers that need to seed sibling fields (unit_price,
     *  name, ...) can do so without a separate lookup. */
    onChange: (productId: string, product?: Product) => void
    /** When set, shows the "Số lượng: X" badge below the combobox once a product
     *  is selected (stock comes from product.stock_available on the list, no
     *  separate fetch). Omit for flows that don't want the selected-stock line. */
    warehouseId?: string
    className?: string
    placeholder?: string
    /** Optional search fanout — some callers use it for analytics or custom
     *  server-side filtering. Ignored for the default in-memory filter. */
    onSearchChange?: (search: string) => void
    /** Product ids to hide from the dropdown — e.g. products already added to a
     *  recipe/discount table. The currently selected `value` is never hidden. */
    excludeProductIds?: string[]
    /** Products to always keep in the options regardless of the current search
     *  result — e.g. a barcode-scanned product that lives outside the search
     *  page. Merged (and de-duped by id) with the fetched list. */
    extraProducts?: Product[]
    /** Show a clear (×) button to reset the selection — used when the picker
     *  acts as an optional list filter ("all products" = empty value). */
    allowClear?: boolean
    /** Chỉ SP thuộc MÃ danh mục này (TUI_12_5G, TUI_KHAC, HOP) — sổ xưởng. */
    categoryCode?: string
    /** Chỉ SP đúng định lượng (gam) — sổ đóng gói khác. Lọc ở server. */
    weightGrams?: number
    /** false → không gọi danh sách (vd chưa nhập định lượng). SP đang chọn vẫn
     *  hiện nhãn nhờ tra theo id. */
    enabled?: boolean
    disabled?: boolean
    /**
     * Ghim `extraProducts` (vd gợi ý tương đồng) lên ĐẦU danh sách thành nhóm
     * riêng, danh sách bên dưới không lặp lại chúng. Khi đang gõ tìm: chỉ hiện
     * kết quả server, gợi ý nào có trong kết quả thì đẩy lên đầu. Mặc định tắt
     * (extra nằm sau danh sách như cũ).
     */
    pinExtraFirst?: boolean
    /** Hiện nhãn "Đang nối: <tên SP KiotViet>" cho SP đã nối mã KiotViet
     *  (popup nối SP, M-145). SP chưa kèm tên (danh sách cuộn) được tra tên
     *  theo mã. Mặc định tắt. */
    showKiotVietLink?: boolean
}

/** Tuỳ chọn hiển thị thêm cho một dòng SP trong dropdown. */
export interface ProductOptionDisplay {
    /** Có giá trị → thêm dòng "Đang nối: <nhãn>" (tên SP KiotViet đang nối). */
    kiotVietLinkLabel?: string | null
}

/**
 * Render dropdown row cho 1 SP: image 40x40 (fallback icon Package) +
 * 2 dòng (name / SKU · KLT · unit). Skip KLT khi weight_value ≤ 0.
 *
 * Exported để mobile/tablet form và các picker khác reuse cùng layout.
 */
export function renderProductOption(
    product: Product,
    display: ProductOptionDisplay = {},
): React.ReactNode {
    const primary =
        product.images?.find((i) => i.is_primary) ?? product.images?.[0]
    const parts: string[] = [product.sku]
    if (product.weight_value && product.weight_value > 0) {
        parts.push(`${product.weight_value} ${product.weight_unit ?? 'kg'}`)
    }
    parts.push(product.unit)
    const meta = parts.join(' · ')

    const stock = product.stock_available ?? 0
    const outOfStock = stock <= 0

    return (
        <div className="flex w-full items-center gap-3">
            {primary ? (
                <img
                    src={getProductImageURL(primary.image_url)}
                    alt={product.name}
                    className="size-10 rounded-md object-cover flex-shrink-0"
                />
            ) : (
                <div className="size-10 rounded-md bg-muted flex items-center justify-center flex-shrink-0">
                    <Package className="size-5 text-muted-foreground" />
                </div>
            )}
            <div className="flex-1 min-w-0">
                <div className="text-sm font-medium break-words">
                    {product.name}
                </div>
                <div className="text-xs text-muted-foreground truncate">
                    {meta}
                </div>
                {display.kiotVietLinkLabel ? (
                    <div className="text-xs text-amber-700 dark:text-amber-400 break-words">
                        {m.product_picker_kiotviet_linked({
                            label: display.kiotVietLinkLabel,
                        })}
                    </div>
                ) : null}
            </div>
            <span
                className={
                    outOfStock
                        ? 'ml-auto flex-shrink-0 text-right text-xs font-medium text-destructive'
                        : 'ml-auto flex-shrink-0 text-right text-xs font-medium text-muted-foreground'
                }
            >
                {m.product_option_stock({ qty: formatQty(stock) })}
            </span>
        </div>
    )
}

/** Tra tên SP KiotViet cho các mã rồi báo lên picker (không render gì). */
function KiotVietNamesLoader({
    codes,
    onLoaded,
}: {
    codes: string[]
    onLoaded: (names: Map<string, KiotVietCodeName> | undefined) => void
}) {
    const { data } = useKiotVietCodeNames(codes, true)
    useEffect(() => {
        onLoaded(data)
    }, [data, onLoaded])
    return null
}

/**
 * ProductPicker — reusable single-product combobox used by order/POS/stock-out
 * flows. Encapsulates the product list fetch + option rendering + selected
 * stock display so callsites only pass `value` and `onChange`. Dropdown option
 * hiển thị image + tên + SKU · KLT · unit; trigger hiển thị chỉ tên SP.
 */
export function ProductPicker({
    id,
    value,
    onChange,
    warehouseId,
    className,
    placeholder,
    onSearchChange,
    excludeProductIds,
    extraProducts,
    allowClear,
    categoryCode,
    weightGrams,
    enabled = true,
    disabled,
    workflow,
    pinExtraFirst = false,
    showKiotVietLink = false,
}: Props) {
    const [search, setSearch] = useState('')
    const [open, setOpen] = useState(false)
    const searchId = useMemo(
        () => (workflow ? crypto.randomUUID() : undefined),
        [workflow, search],
    )
    const lastResult = useRef('')
    const debouncedSearch = useDebounced(search, SEARCH_DEBOUNCE_MS)
    const {
        data: productsRes,
        isFetching,
        isError,
        isPlaceholderData,
        hasNextPage,
        isFetchingNextPage,
        fetchNextPage,
    } = useInfiniteProducts(
        {
            // Trang nhỏ, cuộn tới đáy thì tải tiếp; gõ tìm thì lọc ở server.
            per_page: PAGE_SIZE,
            // Picker chỉ cho chọn SP đang bán — SP ngừng bán (is_active=false) bị
            // loại ở tầng BE qua param này. SP đã ngừng bán mà đang được chọn sẵn
            // vẫn hiển thị nhờ fetch-by-id bên dưới (GetByID không lọc is_active).
            is_active: true,
            ...(categoryCode ? { category_code: categoryCode } : {}),
            ...(weightGrams ? { weight_grams: weightGrams } : {}),
            ...(debouncedSearch ? { search: debouncedSearch } : {}),
        },
        enabled,
    )
    const pages = productsRes?.pages
    const fetched = useMemo(
        () => (pages ? pages.flatMap((p) => p?.data ?? []) : NO_PRODUCTS),
        [pages],
    )

    // Resolve SP đang chọn theo id để nhãn không biến mất khi value là SP KHÔNG
    // có trong danh sách active ở trên — vd sửa đơn cũ có SP đã ngừng bán, hoặc
    // SP nằm ngoài trang search. GetByID không lọc is_active nên luôn resolve
    // được; enabled tự tắt khi value rỗng.
    const { data: selectedById } = useProduct(value)

    // Remember every product we've resolved so the selected one can still be
    // shown in the trigger after the search query changes and no longer
    // returns it. Server search caps the list, so without this the selected
    // label vanishes to the placeholder the moment the user searches elsewhere.
    const seenRef = useRef<Map<string, Product>>(new Map())
    for (const p of fetched) seenRef.current.set(p.id, p)
    if (extraProducts) {
        for (const p of extraProducts) seenRef.current.set(p.id, p)
    }
    // GetByID (useProduct) KHÔNG trả stock_available — BE serialize về 0 (xem
    // product_repository.go: chỉ List/GetByBarcode có cột tồn). Nó chỉ dùng để
    // resolve NHÃN cho SP không nằm trong list (vd SP ngừng bán ở đơn cũ). CHỈ
    // ghi khi chưa có bản nào: nếu ghi đè bản từ list (đã có tồn) thì badge
    // "Số lượng" sẽ hiển thị 0 dù sản phẩm còn hàng.
    if (selectedById && !seenRef.current.has(selectedById.id)) {
        seenRef.current.set(selectedById.id, selectedById)
    }

    const selected = value ? seenRef.current.get(value) : undefined

    // Tra tên qua component con (KiotVietNamesLoader) chỉ mount khi bật
    // showKiotVietLink — picker mặc định không cần QueryClient cho việc này.
    // Tên gộp dồn qua các lô: lô sau lỗi vẫn giữ tên đã có.
    const [kiotVietNames, setKiotVietNames] = useState<
        Map<string, KiotVietCodeName> | undefined
    >()
    const mergeKiotVietNames = useCallback(
        (batch: Map<string, KiotVietCodeName> | undefined) => {
            if (!batch || batch.size === 0) return
            setKiotVietNames((prev) => {
                let next: Map<string, KiotVietCodeName> | undefined
                for (const [code, info] of batch) {
                    const old = prev?.get(code)
                    if (
                        old?.name === info.name &&
                        old?.missing === info.missing
                    )
                        continue
                    next ??= new Map(prev)
                    next.set(code, info)
                }
                return next ?? prev
            })
        },
        [],
    )
    // Tên SP KiotViet cho nhãn "Đang nối": chỉ tra mã CHƯA biết (gợi ý đã kèm
    // tên/cờ mã mất từ API candidates; trang trước đã tra rồi), mỗi lần tối đa
    // KIOTVIET_NAMES_BATCH mã — cuộn hàng trăm SP không gửi lại toàn bộ.
    const codesToName = showKiotVietLink
        ? [
              ...new Set(
                  [...fetched, ...(extraProducts ?? [])]
                      .filter(
                          (p) =>
                              p.kiotviet_sku &&
                              !p.kiotviet_name &&
                              !p.kiotviet_missing,
                      )
                      .map((p) => p.kiotviet_sku as string),
              ),
          ]
              .filter((code) => !kiotVietNames?.has(code))
              .slice(0, KIOTVIET_NAMES_BATCH)
        : []

    const options = useMemo(() => {
        const exclude = new Set(excludeProductIds)
        const display = (p: Product) => ({
            kiotVietLinkLabel:
                showKiotVietLink && p.kiotviet_sku
                    ? kiotVietLinkedLabel(p.kiotviet_sku, {
                          name:
                              p.kiotviet_name ||
                              kiotVietNames?.get(p.kiotviet_sku)?.name,
                          missing:
                              p.kiotviet_missing ||
                              kiotVietNames?.get(p.kiotviet_sku)?.missing,
                      })
                    : null,
        })
        const toOption = (p: Product, group?: string) => ({
            value: p.id,
            label: `${p.name} (${p.unit})`,
            group,
            render: () => renderProductOption(p, display(p)),
        })
        const visible = (p: Product) => p.id === value || !exclude.has(p.id)
        if (pinExtraFirst) {
            // Chưa gõ: gợi ý ghim đầu + toàn bộ danh sách (bỏ trùng). Đang gõ:
            // chỉ kết quả server; gợi ý nào có trong kết quả thì lên đầu.
            const extras = extraProducts ?? []
            const fetchedIds = new Set(fetched.map((p) => p.id))
            const pinned = debouncedSearch
                ? extras.filter((p) => fetchedIds.has(p.id))
                : extras
            const pinnedIds = new Set(pinned.map((p) => p.id))
            // Khử trùng id: các trang tải thêm có thể chồng nhau (SP mới chèn
            // vào lúc đang cuộn làm lệch offset).
            const restById = new Map<string, Product>()
            for (const p of fetched)
                if (!pinnedIds.has(p.id)) restById.set(p.id, p)
            const rest = Array.from(restById.values())
            if (
                selected &&
                !pinnedIds.has(selected.id) &&
                !fetchedIds.has(selected.id)
            )
                rest.push(selected)
            const restGroup =
                pinned.length > 0 ? m.product_picker_all_group() : undefined
            return [
                ...pinned
                    .filter(visible)
                    .map((p) =>
                        toOption(p, m.product_picker_suggested_group()),
                    ),
                ...rest.filter(visible).map((p) => toOption(p, restGroup)),
            ]
        }
        // Current search results + always-on extras + the selected product,
        // de-duped by id. Excluded ids drop out, but never the selected value.
        const map = new Map<string, Product>()
        for (const p of fetched) map.set(p.id, p)
        if (extraProducts) {
            for (const p of extraProducts) map.set(p.id, p)
        }
        if (selected) map.set(selected.id, selected)
        return Array.from(map.values())
            .filter(visible)
            .map((p) => toOption(p))
    }, [
        fetched,
        extraProducts,
        selected,
        excludeProductIds,
        value,
        pinExtraFirst,
        showKiotVietLink,
        kiotVietNames,
        debouncedSearch,
    ])

    const visibleFetched = fetched.filter((product) =>
        options.some((option) => option.value === product.id),
    )
    const settled = Boolean(
        productsRes &&
        search === debouncedSearch &&
        !isFetching &&
        !isError &&
        !isPlaceholderData,
    )
    const hasMore = Boolean(hasNextPage)
    useEffect(() => {
        if (!workflow || !open || !searchId || !settled) return
        const key = `${searchId}:${visibleFetched.length}:${hasMore}`
        if (lastResult.current === key) return
        lastResult.current = key
        workflow.search({
            searchId,
            entityKind: 'product',
            queryKind: search ? 'search' : 'browse',
            queryLength: debouncedSearch.length,
            resultCount: visibleFetched.length,
            hasMore,
        })
    }, [
        workflow,
        open,
        searchId,
        settled,
        visibleFetched.length,
        hasMore,
        search,
        debouncedSearch,
    ])
    const selectedStock = selected?.stock_available

    const handleSearchChange = (q: string) => {
        setSearch(q)
        onSearchChange?.(q)
    }

    const handleChange = (productId: string) => {
        const picked = seenRef.current.get(productId)
        if (picked) {
            workflow?.setContext({ entryMethod: 'manual' })
            const index = options.findIndex(
                (option) => option.value === productId,
            )
            const fromResults = visibleFetched.some(
                (product) => product.id === productId,
            )
            workflow?.step('product_selected', {
                entryMethod: 'manual',
                ...(settled && fromResults && index >= 0
                    ? { searchId, candidateRank: index + 1 }
                    : {}),
            })
        }
        onChange(productId, picked)
    }

    // Ô tìm kiếm nằm trong popover bị Radix unmount khi đóng; reset state search
    // theo, nếu không mở lại thấy ô rỗng mà danh sách vẫn lọc theo từ khoá cũ.
    const handleOpenChange = (nextOpen: boolean) => {
        setOpen(nextOpen)
        if (!nextOpen) {
            setSearch('')
            onSearchChange?.('')
        }
    }

    return (
        <div className={className}>
            {showKiotVietLink ? (
                <KiotVietNamesLoader
                    codes={codesToName}
                    onLoaded={mergeKiotVietNames}
                />
            ) : null}
            <Combobox
                id={id}
                value={value}
                options={options}
                onChange={handleChange}
                onSearchChange={handleSearchChange}
                onOpenChange={handleOpenChange}
                shouldFilter={false}
                onLoadMore={() => void fetchNextPage()}
                hasMore={hasMore}
                loadingMore={isFetchingNextPage}
                disabled={disabled}
                allowClear={allowClear}
                placeholder={
                    placeholder ?? m.order_select_product_placeholder()
                }
                searchPlaceholder={m.order_select_product_placeholder()}
                emptyText={m.product_not_found()}
            />
            {value && selected && warehouseId && (
                <p
                    className={
                        typeof selectedStock === 'number' && selectedStock <= 0
                            ? 'mt-1 text-xs text-destructive'
                            : 'mt-1 text-xs text-muted-foreground'
                    }
                >
                    {m.order_product_stock_badge({
                        qty: formatQty(selectedStock ?? 0),
                        unit: selected.unit,
                    })}
                </p>
            )}
        </div>
    )
}
